/**
 * Карточка изображения `/results/:variantId`: большой кадр со звездой и
 * пересъёмкой, соседи по генерации; справа — «Откуда», «Пост» (текст на продажу
 * и кадр под соцсеть, не у клипов), «Публикации» (ручные отметки), «Как сделано».
 *
 * Видео-клип (`card.video`): вместо картинки — `<video controls poster>` (постер —
 * кадр 0 с), «Переснять в финале» и «Доработать» скрыты, «Как сделано» — параметры клипа из снимка
 * и кадры 1 / 2,5 / 5 с.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowUpFromLine, Clapperboard, Columns2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import { CompareTriptych } from "@/components/results/CompareTriptych";
import { EditPanel } from "@/components/results/EditPanel";
import { EnhancePanel } from "@/components/results/EnhancePanel";
import { enhanceHint } from "@/lib/enhanceGate";
import { PostPanel } from "@/components/results/PostPanel";
import { btn, btnGhost, btnPrimary, ErrorLine, input, Loading } from "@/components/trends/ui";
import { KeyValue, NotFound, Section } from "@/shared/ui";
import { ApiFailure } from "@/lib/api";
import { canMakeVideo } from "@/lib/frameVideo";
import { isRouterModel, reshootFittingVariant, type FitParams } from "@/lib/fitting";
import { mediaUrl, when } from "@/lib/media";
import { loadPhotos, photoKeys } from "@/lib/photos";
import { newKey, usd, type GenInput } from "@/lib/project";
import {
  addPost,
  deletePost,
  loadResultCard,
  NETWORK_LABEL,
  reshootShotVariant,
  resultKeys,
  useMakeVideo,
  resultPath,
  RESULTS_PREFIX,
  setResultDress,
  type ClipParams,
  type ResultCard,
} from "@/lib/results";
import { canCompare } from "@/lib/triptych";
import { markLabel } from "@/lib/verdict";
import { OwnerLinks } from "@/pages/ResultsPage";

/** Ключи пересъёмок — как в FittingFeed: живут до УСПЕШНОГО ответа, замок держит двойной клик. */
const reshootKeys = new Map<number, string>();
const reshootInFlight = new Set<number>();

function reshootKey(variantId: number): string {
  let key = reshootKeys.get(variantId);
  if (!key) {
    key = newKey();
    reshootKeys.set(variantId, key);
  }
  return key;
}

const KIND_LABEL: Record<string, string> = {
  shot: "Кадр",
  fitting: "Примерка",
  reference: "Референс",
  manual: "Вручную",
  clip: "Видео-клип",
  upload: "Загружено",
  enhance: "Улучшено",
  other: "Другое",
};
const ACTIVE = new Set(["queued", "running"]);

const STATUS_LABEL: Record<string, string> = {
  queued: "в очереди",
  running: "генерируется",
  done: "готово",
  failed: "ошибка",
};

function today(): string {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Moscow" });
}

/** Состояние (форма, ошибки, мутации) живёт в теле с `key` по варианту: переход A → B его не наследует. */
export function ResultCardPage() {
  const variantId = Number(useParams().variantId);
  if (!Number.isInteger(variantId) || variantId <= 0) return <NotFound />;
  return <ResultCardBody key={variantId} variantId={variantId} />;
}

type ReshootVars = { id: number; kind: string };
type ReshootResult = { error?: string; gens?: { generation_id: number }[] };

function ResultCardBody({ variantId }: { variantId: number }) {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: resultKeys.card(variantId),
    queryFn: () => loadResultCard(variantId),
    retry: (n, e) => !(e instanceof ApiFailure && e.status === 404) && n < 2,
    // пока пересъёмка в очереди или в работе, карточка сама узнаёт о готовности
    refetchInterval: (query) =>
      query.state.data?.reshoots.some((r) => ACTIVE.has(r.status)) ||
      query.state.data?.edits?.some((r) => ACTIVE.has(r.status)) ||
      query.state.data?.enhances?.some((r) => ACTIVE.has(r.status))
        ? 3_000
        : false,
  });

  const refresh = () => client.invalidateQueries({ queryKey: RESULTS_PREFIX });
  const [lastError, setLastError] = useState("");
  const [queuedGen, setQueuedGen] = useState<number | "unknown" | null>(null);
  const [compareAt, setCompareAt] = useState<GenInput | "open" | null>(null);
  const navigate = useNavigate();
  // «Сделать видео» заводит проект СРАЗУ, без окна с галочками (задача Оли №305: «я выбираю
  // генерацию, затем перехожу в окно проекта — без попапа»). Умолчания даёт сервер (все ракурсы,
  // кусок `SHOT_MS_DEFAULT`, потолок), а состав ракурсов, модель и длительности правятся на
  // странице проекта — там это видно и бесплатно.
  const makeVideo = useMakeVideo(variantId, ({ project_id }) => void navigate(`/project/${project_id}?tab=shots`));

  const reshoot = useMutation<ReshootResult, Error, ReshootVars>({
    mutationFn: ({ id, kind }) =>
      kind === "fitting" || kind === "reference" ? reshootFittingVariant(id, reshootKey(id)) : reshootShotVariant(id, reshootKey(id)),
    onSuccess: (got, { id }) => {
      setLastError(got.error ?? "");
      if (got.error) return; // ключ не сбрасываем: повтор пойдёт с ним же
      reshootKeys.delete(id);
      const ids = (got.gens ?? []).map((x) => x.generation_id);
      setQueuedGen(ids.length > 0 ? Math.max(...ids) : "unknown");
      return client.invalidateQueries({ queryKey: resultKeys.card(id) }).then(refresh);
    },
    onSettled: (_data, _error, { id }) => {
      reshootInFlight.delete(id);
    },
  });
  const onReshoot = (kind: string) => {
    if (reshootInFlight.has(variantId)) return;
    reshootInFlight.add(variantId);
    setQueuedGen(null);
    reshoot.mutate({ id: variantId, kind });
  };

  if (q.isError) {
    if (q.error instanceof ApiFailure && q.error.status === 404) return <NotFound />;
    return (
      <div className="p-5">
        <ErrorLine error={q.error} />
      </div>
    );
  }
  const card = q.data;
  if (!card) {
    return (
      <div className="p-5">
        <Loading />
      </div>
    );
  }

  const { variant, generation: g } = card;
  const reshootBusy = card.reshoots.some((r) => ACTIVE.has(r.status));
  // «Сделать видео» (видео-ролик из готового кадра, §3/§10): клип, видео-вариант и незавершённая
  // генерация не годятся опорным кадром ролика — тот же гейт, что сервер держит на создании проекта.
  const videoGate = canMakeVideo(g.kind, g.status, card.video != null);
  const aspect = variant.size === "1024x1536" ? "aspect-[2/3]" : "aspect-[9/16]";
  const ratioStyle = !variant.size && g.aspect_ratio ? { aspectRatio: g.aspect_ratio.replace(":", " / ") } : undefined;

  return (
    <div className="grid gap-3 p-5" data-annot="карточка изображения">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/results" className="text-[12.5px] text-accent-text hover:underline">
          ← Все результаты
        </Link>
        {/* Задача Оли №313: «нет кнопки чтобы вернуться назад к проекту, только в результаты —
            приходится открывать проект заново». Кадр ракурса принадлежит кадру проекта, и путь
            назад обязан быть здесь же. Ведём на «Кадры» — вкладку, с которой сюда и приходят. */}
        {card.owner.project ? (
          <Link
            to={`/project/${card.owner.project.id}?tab=shots`}
            className="text-[12.5px] text-accent-text hover:underline"
            data-annot="назад в проект"
          >
            ← К проекту «{card.owner.project.name}»
          </Link>
        ) : null}
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <div className="grid gap-2">
          {card.video ? (
            <video
              controls
              muted
              playsInline
              preload="metadata"
              src={mediaUrl(variant.asset_path)}
              poster={posterOf(card.video.frames)}
              className="aspect-[9/16] w-full rounded-[var(--radius-ui)] border border-border bg-black object-contain"
            />
          ) : (
            <a href={mediaUrl(variant.asset_path)} target="_blank" rel="noreferrer" className="relative block">
              {variant.pick === "picked" || variant.pick === "rejected" ? (
                <span
                  className={clsx(
                    "absolute top-2 left-2 rounded-[3px] px-1.5 py-0.5 text-[11px] font-medium",
                    "border border-border bg-bg text-fg",
                  )}
                  data-annot="отметка"
                >
                  {markLabel(g.kind, variant.pick)}
                </span>
              ) : null}
              <img
                src={mediaUrl(variant.asset_path)}
                alt=""
                style={ratioStyle}
                className={clsx(ratioStyle ? null : aspect, "w-full rounded-[var(--radius-ui)] border border-border object-cover")}
              />
            </a>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canCompare(card) ? (
              <button type="button" className={btn} onClick={() => setCompareAt("open")} data-annot="сравнить">
                <Columns2 size={13} />
                Сравнить
              </button>
            ) : null}
            {videoGate.ok ? (
              <button
                type="button"
                className={btn}
                disabled={makeVideo.busy}
                onClick={() => makeVideo.run({})}
                data-annot="сделать видео"
              >
                <Clapperboard size={13} />
                {makeVideo.busy ? "Создаю ролик…" : "Сделать видео"}
              </button>
            ) : null}
            {/* загруженное и улучшенное не пересъёмываются — сервер откажет */}
            {g.kind === "manual" || g.kind === "clip" || g.kind === "upload" || g.kind === "enhance" || card.video || isRouterModel(g.model) ? null : (
            <button
              type="button"
              className={btn}
              disabled={reshoot.isPending || reshootBusy || !!card.owner.reference?.deleted}
              title={
                card.owner.reference?.deleted ? "референс удалён" : reshootBusy ? "пересъёмка уже в очереди" : undefined
              }
              onClick={() => onReshoot(g.kind)}
            >
              <ArrowUpFromLine size={12} />
              Переснять в финале
            </button>
            )}
            {queuedGen !== null ? (
              <span className="text-[12px] text-fg-muted" data-annot="пересъёмка поставлена">
                {queuedGen === "unknown" ? "Поставлено в очередь" : `Поставлено: генерация #${queuedGen}`}
              </span>
            ) : null}
          </div>
          <ErrorLine error={reshoot.error} />
          <ErrorLine error={makeVideo.failure} />
          {lastError ? <ErrorLine error={new Error(lastError)} /> : null}
          {card.siblings.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" data-annot="соседи">
              {card.siblings.map((s) => (
                <Link key={s.id} to={resultPath(s.id)} title="другой вариант этой генерации">
                  <img
                    src={mediaUrl(s.poster_path ?? s.asset_path)}
                    alt=""
                    className={clsx(
                      "h-20 w-auto rounded-[4px] border object-cover",
                      s.pick === "rejected" && "opacity-50",
                      s.pick === "picked" ? "border-accent ring-1 ring-accent" : "border-border",
                    )}
                  />
                </Link>
              ))}
            </div>
          ) : null}
        </div>

        <div className="grid min-w-0 gap-3">
          <Origin card={card} />
          {/* клип (mp4) не дорабатывается: сервер откажет 400, перезапуск — на вкладке «Видео» */}
          {!card.video && (g.kind === "fitting" || g.kind === "reference" || g.kind === "manual") ? (
            <Section title="Доработать">
              <EditPanel variantId={variantId} model={g.model} runs={card.edits ?? []} onStarted={refresh} />
            </Section>
          ) : null}
          {/* улучшение (Krea) — только готовая картинка: клип и недоделанная генерация не улучшаются;
              кнопка — у отмеченного «В улучшение» или загруженного и пока не улучшенного, иначе подсказка (`enhanceHint`) */}
          {!card.video && g.kind !== "clip" && g.status === "done" ? (
            <Section title="Улучшить">
              <EnhancePanel
                variantId={variantId}
                ready={card.krea_ready ?? false}
                hint={enhanceHint(g.kind, variant.pick, card.enhances ?? [])}
                runs={card.enhances ?? []}
                onStarted={refresh}
              />
            </Section>
          ) : null}
          {card.video || g.kind === "clip" ? null : (
            <Section title="Пост">
              <PostPanel variantId={variantId} assetPath={variant.asset_path} />
            </Section>
          )}
          <Posts card={card} variantId={variantId} onChanged={refresh} />
          {g.kind === "upload" ? null : card.video ? (
            <ClipMade card={card} video={card.video} clip={card.clip ?? null} />
          ) : (
            <HowMade card={card} onOpenInput={setCompareAt} />
          )}
        </div>
      </div>
      {compareAt !== null && canCompare(card) ? (
        <CompareTriptych card={card} startInput={compareAt === "open" ? undefined : compareAt} onClose={() => setCompareAt(null)} />
      ) : null}
    </div>
  );
}

const NO_TAKEN: readonly number[] = [];
const NO_NOTES: ReadonlyMap<number, string> = new Map();

/** Платье загруженного фото: сменить или отвязать — это фото или всю загрузку (`results.set_dress`). У генераций
 *  платье — вход модели, его не меняют. «Пост» после смены перечитывается: серия и цена — от нового платья. */
function UploadDress({ card }: { card: ResultCard }) {
  const client = useQueryClient();
  const vid = card.variant.id;
  const dress = card.owner.dress;
  const total = card.siblings.length + 1;
  const [whole, setWhole] = useState(false);
  const [picker, setPicker] = useState(false);
  const list = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, enabled: picker });
  const save = useMutation({
    mutationFn: (dressId: number | null) => setResultDress(vid, dressId, whole && total > 1),
    onSuccess: (next) => {
      client.setQueryData(resultKeys.card(vid), next);
      void client.invalidateQueries({ queryKey: RESULTS_PREFIX });
    },
  });
  return (
    <>
      <KeyValue k="Платье">
        <span className="flex flex-wrap items-center gap-2">
          {dress ? (
            <Link to={`/photos/${dress.id}`} className="text-accent-text hover:underline">
              {dress.name ?? `Платье #${dress.id}`}
            </Link>
          ) : (
            <span className="text-fg-muted">без платья</span>
          )}
          <button type="button" className={btn} disabled={save.isPending} onClick={() => setPicker(true)}>
            {dress ? "Сменить" : "Выбрать"}
          </button>
          {dress ? (
            <button type="button" className={btnGhost} disabled={save.isPending} onClick={() => save.mutate(null)}>
              Отвязать
            </button>
          ) : null}
        </span>
      </KeyValue>
      {total > 1 ? (
        <KeyValue k="Менять у">
          <span className="flex flex-wrap gap-3">
            <label className="flex items-center gap-1">
              <input type="radio" checked={!whole} onChange={() => setWhole(false)} /> только этого фото
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" checked={whole} onChange={() => setWhole(true)} /> всех {total} фото загрузки
            </label>
          </span>
        </KeyValue>
      ) : null}
      <ErrorLine error={save.error} />
      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={list.data}
        taken={NO_TAKEN}
        annotate={NO_NOTES}
        triedAll={null}
        single
        onAdd={(ids) => {
          const id = ids[0];
          if (id !== undefined && id !== dress?.id) save.mutate(id);
        }}
      />
    </>
  );
}

function Origin({ card }: { card: ResultCard }) {
  const { generation: g, owner } = card;
  const link = "text-accent-text hover:underline";
  return (
    <Section title="Откуда">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-fg-muted">
        <span className="rounded-[3px] bg-bg-elevated px-1.5 text-[11px] font-medium text-fg">
          {KIND_LABEL[g.kind] ?? g.kind}
        </span>
        <OwnerLinks kind={g.kind} owner={owner} />
      </div>
      {g.kind === "upload" ? (
        <>
          <KeyValue k={card.origin?.label ?? "загружено вручную"}>
            {g.author_name}
            {card.origin?.note ? ` · ${card.origin.note}` : ""}
          </KeyValue>
          <UploadDress card={card} />
        </>
      ) : null}
      {card.reshoot_of ? (
        <KeyValue k="Переснято из">
          <Link to={resultPath(card.reshoot_of.variant_id)} className={link}>
            изображение #{card.reshoot_of.variant_id}
          </Link>
        </KeyValue>
      ) : null}
      {card.edit_of ? (
        <KeyValue k="Доработано из">
          <Link to={resultPath(card.edit_of.variant_id)} className={link}>
            #{card.edit_of.variant_id}
          </Link>
          {card.edit_of.note ? ` — «${card.edit_of.note}»` : ""}
        </KeyValue>
      ) : null}
      {card.enhance_of ? (
        <KeyValue k="Улучшено из">
          <span className="mr-2 rounded-[3px] bg-bg-elevated px-1.5 text-[11px] font-medium text-fg">улучшено</span>
          <Link to={resultPath(card.enhance_of.variant_id)} className={link}>
            #{card.enhance_of.variant_id}
          </Link>
        </KeyValue>
      ) : null}
      {card.enhances && card.enhances.length > 0 ? (
        <KeyValue k="Улучшения">
          <span className="flex flex-wrap gap-x-3 gap-y-1">
            {card.enhances.map((r) =>
              r.variants.length > 0 ? (
                r.variants.map((id) => (
                  <Link key={id} to={resultPath(id)} className={link}>
                    изображение #{id}
                  </Link>
                ))
              ) : (
                <span key={r.generation_id} className="text-fg-muted">
                  генерация #{r.generation_id}: {STATUS_LABEL[r.status] ?? r.status}
                </span>
              ),
            )}
          </span>
        </KeyValue>
      ) : null}
      {card.edits && card.edits.length > 0 ? (
        <KeyValue k="Доработки">
          <span className="flex flex-wrap gap-x-3 gap-y-1">
            {card.edits.map((r) =>
              r.variants.length > 0 ? (
                r.variants.map((id) => (
                  <Link key={id} to={resultPath(id)} className={link} title={r.note ?? undefined}>
                    изображение #{id}
                  </Link>
                ))
              ) : (
                <span key={r.generation_id} className="text-fg-muted">
                  генерация #{r.generation_id}: {STATUS_LABEL[r.status] ?? r.status}
                </span>
              ),
            )}
          </span>
        </KeyValue>
      ) : null}
      {card.reshoots.length > 0 ? (
        <KeyValue k="Пересняли в">
          <span className="flex flex-wrap gap-x-3 gap-y-1">
            {card.reshoots.map((r) =>
              r.variants.length > 0 ? (
                r.variants.map((id) => (
                  <Link key={id} to={resultPath(id)} className={link}>
                    изображение #{id}
                  </Link>
                ))
              ) : (
                <span key={r.generation_id} className="text-fg-muted">
                  генерация #{r.generation_id}: {STATUS_LABEL[r.status] ?? r.status}
                </span>
              ),
            )}
          </span>
        </KeyValue>
      ) : null}
    </Section>
  );
}

function Posts({ card, variantId, onChanged }: { card: ResultCard; variantId: number; onChanged: () => unknown }) {
  const client = useQueryClient();
  const [network, setNetwork] = useState("");
  const [url, setUrl] = useState("");
  const [date, setDate] = useState(today);
  const [confirming, setConfirming] = useState<number | null>(null);

  const apply = (next: ResultCard) => {
    client.setQueryData(resultKeys.card(variantId), next);
    return onChanged();
  };
  const chosen = network || card.networks[0]?.code || "";
  const add = useMutation({
    mutationFn: () =>
      addPost(variantId, {
        network: chosen,
        ...(url.trim() ? { url: url.trim() } : {}),
        ...(date ? { date } : {}),
      }),
    onSuccess: (next) => {
      setUrl("");
      return apply(next);
    },
  });
  const del = useMutation({
    mutationFn: (postId: number) => deletePost(postId),
    onSuccess: (next) => {
      setConfirming(null);
      return apply(next);
    },
  });

  return (
    <Section title="Публикации">
      {card.posts.length === 0 ? (
        <p className="text-[12.5px] text-fg-muted">Пока нигде не опубликовано</p>
      ) : (
        <ul className="grid gap-1">
          {card.posts.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
              <span className="rounded-[3px] bg-bg-elevated px-1.5 text-[11px] font-medium text-fg" title={p.network.name}>
                {NETWORK_LABEL[p.network.code] ?? p.network.code}
              </span>
              <span className="text-fg">{p.network.name}</span>
              {p.url ? (
                <a href={p.url} target="_blank" rel="noreferrer" className="min-w-0 truncate text-accent-text hover:underline">
                  {p.url}
                </a>
              ) : null}
              {p.date ? <span className="tnum text-fg-muted">{p.date}</span> : null}
              <span className="text-fg-faint">{p.author_name}</span>
              {confirming === p.id ? (
                <span className="inline-flex items-center gap-1">
                  <span className="text-fg-muted">Удалить?</span>
                  <button type="button" className={btnGhost} disabled={del.isPending} onClick={() => del.mutate(p.id)}>
                    Да
                  </button>
                  <button type="button" className={btnGhost} disabled={del.isPending} onClick={() => setConfirming(null)}>
                    Нет
                  </button>
                </span>
              ) : (
                <button type="button" className={btnGhost} onClick={() => setConfirming(p.id)}>
                  Удалить
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorLine error={del.error} />
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!add.isPending) add.mutate();
        }}
      >
        <select aria-label="Сеть" className={input} value={chosen} onChange={(e) => setNetwork(e.target.value)}>
          {card.networks.map((n) => (
            <option key={n.code} value={n.code}>
              {n.name}
            </option>
          ))}
        </select>
        <input
          aria-label="Ссылка"
          className={clsx(input, "w-64 max-w-full")}
          placeholder="Ссылка на публикацию"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <input aria-label="Дата" type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} />
        <button type="submit" className={btnPrimary} disabled={add.isPending}>
          {add.isPending ? "Отмечаю…" : "Отметить публикацию"}
        </button>
      </form>
      <ErrorLine error={add.error} />
    </Section>
  );
}

/** Параметры запуска по таблице спеки: у OpenAI пресет/размер, у OpenRouter пропорция/разрешение. */
function LaunchParams({ p, catalog }: { p: FitParams; catalog: { code: string; label: string }[] }) {
  const router = p.provider === "openrouter";
  const known = new Set(catalog.map((r) => r.code));
  const on = new Set(p.rules);
  const unknown = p.rules.filter((c) => !known.has(c));
  return (
    <>
      {router ? null : <KeyValue k="Пресет">{p.preset ?? "—"}</KeyValue>}
      <KeyValue k="Качество">{p.quality || (router ? "— (у модели нет)" : "—")}</KeyValue>
      {router ? (
        <>
          <KeyValue k="Пропорция">{p.aspect_ratio ?? "—"}</KeyValue>
          <KeyValue k="Разрешение">{p.resolution ?? "—"}</KeyValue>
        </>
      ) : (
        <KeyValue k="Размер">{p.size_mode === "auto" ? `по пропорции сцены → ${p.size}` : p.size}</KeyValue>
      )}
      <KeyValue k="Кадров на пару">{p.n}</KeyValue>
      <KeyValue k="Вход, px">{p.fit_px}</KeyValue>
      <KeyValue k="Правила">
        {catalog.length === 0 && unknown.length === 0 ? (
          "—"
        ) : (
          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
            {catalog.map((r) => (
              <span key={r.code} className={on.has(r.code) ? "text-fg" : "text-fg-faint line-through"}>
                {r.label}
              </span>
            ))}
            {unknown.map((c) => (
              <span key={c} className="font-mono text-fg">
                {c}
              </span>
            ))}
          </span>
        )}
      </KeyValue>
    </>
  );
}

function HowMade({ card, onOpenInput }: { card: ResultCard; onOpenInput: (i: GenInput) => void }) {
  const { generation: g, owner } = card;
  const p = g.params ?? null;
  const [copied, setCopied] = useState(false);
  const actual = g.cost_actual !== null && g.cost_actual !== undefined;
  const total = actual ? usd(g.cost_actual) : `~${usd(g.cost_estimate)}`;
  const each =
    g.cost_each !== null && g.cost_each !== undefined ? (actual ? usd(g.cost_each) : `~${usd(g.cost_each)}`) : "—";
  const usage = g.usage;
  const tok = (k: string): string | null => {
    const v = usage?.[k];
    return typeof v === "number" ? v.toLocaleString("ru-RU") : null;
  };
  const tokens = [
    tok("input_tokens") ? `вход ${tok("input_tokens")}` : null,
    tok("output_tokens") ? `выход ${tok("output_tokens")}` : null,
    tok("total_tokens") ? `всего ${tok("total_tokens")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const showTemplate = !!g.prompt_template && g.prompt_template !== g.prompt;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(g.prompt ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Section title="Как сделано">
      <KeyValue k="Модель">{g.model}</KeyValue>
      {g.edit_note ? <KeyValue k="Замечание">{g.edit_note}</KeyValue> : null}
      {p ? (
        <LaunchParams p={p} catalog={g.rules_catalog ?? []} />
      ) : (
        <>
          {g.quality ? <KeyValue k="Качество">{g.quality}</KeyValue> : null}
          {g.size ? <KeyValue k="Размер">{g.size}</KeyValue> : <KeyValue k="Пропорция">{g.aspect_ratio ?? "—"}</KeyValue>}
          {g.preset ? <KeyValue k="Пресет">{g.preset}</KeyValue> : null}
          <KeyValue k="n">{g.n}</KeyValue>
        </>
      )}
      <KeyValue k="Цена за кадр">{each}</KeyValue>
      <KeyValue k="Цена за генерацию">{total}</KeyValue>
      {usage ? <KeyValue k="Токены">{tokens || JSON.stringify(usage)}</KeyValue> : null}
      <KeyValue k="Автор">{g.author_name}</KeyValue>
      <KeyValue k="Поставлено">{when(g.date_create)}</KeyValue>
      <KeyValue k="Начато">{when(g.date_start)}</KeyValue>
      <KeyValue k="Готово">{when(g.date_finish)}</KeyValue>
      {owner.photo_ids.length > 0 ? <KeyValue k="Фото платья">{owner.photo_ids.map((i) => `#${i}`).join(", ")}</KeyValue> : null}
      {g.inputs.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-annot="входы">
          {g.inputs.map((i) => (
            <figure key={`${i.asset_id}-${i.role}`} className="m-0 grid w-20 gap-0.5">
              <button
                type="button"
                className="cursor-zoom-in"
                onClick={() => onOpenInput(i)}
                aria-label={`Сравнить: ${i.label || i.role}`}
                title={i.hint ? `${i.label}: ${i.hint}` : i.label}
              >
                <img
                  src={mediaUrl(i.path)}
                  alt=""
                  className="h-24 w-20 rounded-[4px] border border-border object-cover"
                />
              </button>
              <figcaption className="text-[11px] leading-tight text-fg-muted">
                {i.role}
                {i.label ? ` · ${i.label}` : ""}
                {i.hint ? ` · ${i.hint}` : ""}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
      {g.prompt ? (
        <div className="grid gap-1">
          <div className="flex items-center justify-between">
            <span className="text-[11.5px] text-fg-faint">
              Промпт
              {p?.prompt_edited ? (
                <span className="ml-2 rounded-[4px] bg-warn-bg px-1.5 py-0.5 text-[11px] font-medium text-warn">свой</span>
              ) : null}
            </span>
            <button type="button" className={btnGhost} onClick={copy}>
              {copied ? "Скопировано" : "Копировать"}
            </button>
          </div>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-elevated p-2 text-[12px] text-fg">
            {g.prompt}
          </pre>
        </div>
      ) : null}
      {showTemplate ? (
        <div className="grid gap-1">
          <span className="text-[11.5px] text-fg-faint">Шаблон</span>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-elevated p-2 text-[12px] text-fg-muted">
            {g.prompt_template}
          </pre>
        </div>
      ) : null}
      {g.error ? <ErrorLine error={new Error(g.error)} /> : null}
    </Section>
  );
}

type CardVideo = NonNullable<ResultCard["video"]>;

/** Уровни видеомоделей (`videogen.TIER_LABEL`). */
const TIER_LABEL: Record<string, string> = { cheap: "дешёвая", value: "ценная", mid: "средняя", premium: "дорогая" };

/** Постер — кадр 0 с; нет — браузер покажет первый кадр сам. */
function posterOf(frames: CardVideo["frames"]): string | undefined {
  const zero = frames.find((f) => f.ms === 0);
  return zero ? mediaUrl(zero.path) : undefined;
}

const sec = (ms: number | null | undefined): string =>
  ms === null || ms === undefined ? "—" : `${(ms / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} с`;

/**
 * «Как сделано» у клипа: модель, размер/разрешение, длительность клипа (за что платили) и файла, fps,
 * бесшовно, кадрирование, негатив, стилл-источник, кадры 1 / 2,5 / 5 с, промпт, цена, время.
 */
function ClipMade({ card, video, clip: s }: { card: ResultCard; video: CardVideo; clip: ClipParams | null }) {
  const { generation: g } = card;
  const link = "text-accent-text hover:underline";
  const actual = g.cost_actual !== null && g.cost_actual !== undefined;
  const frames = video.frames.filter((f) => f.ms > 0);
  const crop =
    s?.frame_crop === "below_chin"
      ? `ниже подбородка — срез сверху ${Math.round((s.crop_top ?? 0) * 100)} %`
      : s?.frame_crop === "none"
        ? "без среза"
        : "—";
  const seamless =
    s?.seamless === null || s?.seamless === undefined
      ? "—"
      : !s.seamless
        ? "нет"
        : s.seamless_applied
          ? "да (last_frame — стилл следующего кадра)"
          : "запрошено, не применено";

  return (
    <Section title="Как сделано">
      <KeyValue k="Модель">
        {g.model}
        {s?.tier ? ` · уровень ${TIER_LABEL[s.tier] ?? s.tier}` : ""}
      </KeyValue>
      <KeyValue k="Размер">
        {s?.size ?? s?.resolution ?? g.size ?? "—"}
        {s?.aspect_ratio ? ` · ${s.aspect_ratio}` : ""}
        {video.width && video.height ? ` · файл ${video.width}×${video.height}` : ""}
      </KeyValue>
      <KeyValue k="Длительность">
        клип {s?.duration_s != null ? `${s.duration_s} с` : "—"} · файл {sec(video.duration_ms)}
        {s?.shot_duration_ms ? ` · кадр ${sec(s.shot_duration_ms)}` : ""}
      </KeyValue>
      <KeyValue k="fps">{video.fps !== null ? video.fps.toLocaleString("ru-RU", { maximumFractionDigits: 3 }) : "—"}</KeyValue>
      <KeyValue k="Бесшовно">{seamless}</KeyValue>
      <KeyValue k="Кадрирование">{crop}</KeyValue>
      <KeyValue k="Референсы платья">{s?.with_refs == null ? "—" : s.with_refs ? "да" : "нет"}</KeyValue>
      {s?.audio_track != null ? <KeyValue k="Звук в файле">{s.audio_track ? "есть" : "нет"}</KeyValue> : null}
      {s?.camera ? <KeyValue k="Камера">{s.camera}</KeyValue> : null}
      <KeyValue k="Негатив">{s?.negative || "—"}</KeyValue>
      <KeyValue k="Стилл">
        {s?.still ? (
          <Link to={resultPath(s.still.variant_id)} className={link}>
            изображение #{s.still.variant_id}
          </Link>
        ) : (
          "—"
        )}
        {s?.next_still ? (
          <>
            {" → "}
            <Link to={resultPath(s.next_still.variant_id)} className={link}>
              #{s.next_still.variant_id}
            </Link>
          </>
        ) : null}
      </KeyValue>
      {s?.retry_of ? <KeyValue k="Повтор">генерации #{s.retry_of}</KeyValue> : null}
      <KeyValue k="Цена">{actual ? usd(g.cost_actual) : `~${usd(g.cost_estimate)}`}</KeyValue>
      <KeyValue k="Автор">{g.author_name}</KeyValue>
      <KeyValue k="Поставлено">{when(g.date_create)}</KeyValue>
      <KeyValue k="Отправлено">{when(g.date_start)}</KeyValue>
      <KeyValue k="Готово">{when(g.date_finish)}</KeyValue>
      {frames.length > 0 ? (
        <div className="grid gap-1" data-annot="кадры клипа">
          <span className="text-[11.5px] text-fg-faint">Кадры 1 / 2,5 / 5 с — платье то же, лицо одно, лишних нет</span>
          <div className="flex flex-wrap gap-2">
            {frames.map((f) => (
              <a key={f.ms} href={mediaUrl(f.path)} target="_blank" rel="noreferrer" className="grid w-24 gap-0.5">
                <img
                  src={mediaUrl(f.path)}
                  alt=""
                  className="aspect-[9/16] w-full rounded-[4px] border border-border object-cover hover:border-accent"
                />
                <span className="text-[11px] text-fg-muted tnum">{sec(f.ms)}</span>
              </a>
            ))}
          </div>
        </div>
      ) : null}
      {g.inputs.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-annot="входы">
          {g.inputs.map((i) => (
            <figure key={`${i.asset_id}-${i.role}`} className="m-0 grid w-20 gap-0.5">
              <img
                src={mediaUrl(i.path)}
                alt=""
                title={i.hint ? `${i.label}: ${i.hint}` : i.label}
                className="h-24 w-20 rounded-[4px] border border-border object-cover"
              />
              <figcaption className="text-[11px] leading-tight text-fg-muted">
                {i.role}
                {i.label ? ` · ${i.label}` : ""}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
      {g.prompt ? (
        <div className="grid gap-1">
          <span className="text-[11.5px] text-fg-faint">Промпт (ушёл провайдеру)</span>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-elevated p-2 text-[12px] text-fg">
            {g.prompt}
          </pre>
        </div>
      ) : null}
      {g.error ? <ErrorLine error={new Error(g.error)} /> : null}
    </Section>
  );
}
